

import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const REDIRECT_DELAY_MS = 2500;

const NO_SESSION_KEYWORDS = [
  "no active",
  "no ongoing",
  "not found",
  "no session",
  "session not found",
];

/**
 * Returns true if the error indicates the operator's session is gone.
 * Pure function — exported for unit testing if you ever want to add tests.
 */
export const isSessionLossError = (err) => {
  const status = err?.response?.status;
  const message = (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    ""
  ).toLowerCase();

  if (status === 401 || status === 403) return true;
  if (status === 404) return true;
  if (NO_SESSION_KEYWORDS.some((kw) => message.includes(kw))) return true;
  return false;
};

/**
 * React hook. Returns a stable callback that:
 *   - inspects the error
 *   - if session loss: schedules a redirect to "/" after a brief delay,
 *     and returns true so the caller can `return` early
 *   - otherwise: returns false (caller should handle the error normally)
 *
 * The redirect timer is cleaned up automatically if the component unmounts.
 */
export default function useSessionLossRedirect() {
  const navigate = useNavigate();
  const timerRef = useRef(null);

  // Clear any pending redirect when the component unmounts.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback(
    (err) => {
      if (!isSessionLossError(err)) return false;

      // If a redirect is already scheduled, don't stack another.
      if (timerRef.current) return true;

      timerRef.current = setTimeout(() => {
        navigate("/", { replace: true });
      }, REDIRECT_DELAY_MS);
      return true;
    },
    [navigate]
  );
}